import { api } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';

/**
 * Serviço para gerenciar entidades
 */
export const entityService = {
  /**
   * Busca as entidades do usuário logado
   */
  async getMyEntities() {
    try {
      const response = await api.get(ENDPOINTS.ENTITIES_MY);
      
      if (response.data?.error) {
        throw new Error('Erro ao buscar entidades');
      }

      return {
        success: true,
        entities: response.data?.response || [],
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao buscar entidades',
      };
    }
  },

  /**
   * Busca uma entidade específica
   */
  async getEntity(id) {
    try {
      const response = await api.get(`${ENDPOINTS.ENTITIES}/${id}`);
      
      if (response.data.error) {
        throw new Error('Erro ao buscar entidade');
      }
      
      return {
        success: true,
        entity: response.data.response,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao buscar entidade',
      };
    }
  },

  /**
   * Atualiza o status ativo da entidade
   */
  async toggleEntityStatus(entityId, active) {
    try {
      const response = await api.put(ENDPOINTS.ENTITIES, { 
        id: entityId,
        active: active 
      });
      
      if (response.data?.error) {
        throw new Error('Erro ao atualizar status');
      }

      return {
        success: true,
        entity: response.data.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar status',
      };
    }
  },

  /**
   * Atualiza as configurações de agendamento da entidade
   */
  async updateScheduleSettings(entityId, scheduleData) {
    try {
      const response = await api.put(ENDPOINTS.ENTITIES, {
        id: entityId,
        scheduleDays: scheduleData.scheduleDays,
        scheduleStart: scheduleData.scheduleStart,
        scheduleEnd: scheduleData.scheduleEnd,
        scheduleRange: scheduleData.scheduleRange,
        scheduleActive: scheduleData.scheduleActive
      });
      
      if (response.data?.error) {
        throw new Error('Erro ao atualizar configurações');
      }

      return {
        success: true,
        entity: response.data.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar configurações',
      };
    }
  },

  /**
   * Busca os planos de saúde disponíveis
   */
  async getInsurances() {
    try {
      const response = await api.get(ENDPOINTS.ENTITIES_INSURANCES);
      
      if (response.data?.error) {
        throw new Error('Erro ao buscar planos de saúde');
      }

      return {
        success: true,
        insurances: response.data?.response || [],
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao buscar planos de saúde',
      };
    }
  },

  /**
   * Atualiza os planos de saúde da entidade
   */
  async updateInsurances(entityId, insuranceIds) {
    try {
      const insurances = insuranceIds.map(id => ({
        entityId,
        insuranceId: id,
        additionalData: null
      }));

      const response = await api.post(ENDPOINTS.ENTITIES_INSURANCES, {
        entityId,
        insurances
      });
      
      if (response.data?.error) {
        throw new Error('Erro ao atualizar planos de saúde');
      }

      return {
        success: true,
        response: response.data?.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar planos de saúde',
      };
    }
  },

  /**
   * Atualiza os planos de saúde da entidade com dados adicionais
   */
  async updateInsurancesWithData(entityId, insurancesData) {
    try {
      const insurances = insurancesData.map(item => ({
        entityId,
        insuranceId: item.insuranceId,
        additionalData: item.additionalData || null
      }));

      const response = await api.post(ENDPOINTS.ENTITIES_INSURANCES, {
        entityId,
        insurances
      });
      
      if (response.data?.error) {
        throw new Error('Erro ao atualizar planos de saúde');
      }

      return {
        success: true,
        response: response.data?.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar planos de saúde',
      };
    }
  },

  /**
   * Cria uma nova entidade
   */
  async createEntity(entityData) {
    try {
      const response = await api.post(ENDPOINTS.ENTITIES, entityData);
      
      if (response.data?.error) {
        throw new Error('Erro ao criar entidade');
      }

      return {
        success: true,
        entity: response.data.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao criar entidade',
      };
    }
  },

  /**
   * Atualiza os dados básicos da entidade
   */
  async updateBasicData(entityId, basicData) {
    try {
      const response = await api.put(ENDPOINTS.ENTITIES, {
        id: entityId,
        name: basicData.name,
        phone: basicData.phone,
        address: basicData.address,
        addressNumber: basicData.addressNumber,
        addressComplement: basicData.addressComplement,
        addressCity: basicData.addressCity,
        addressUF: basicData.addressUF,
        addressZIP: basicData.addressZIP,
        addressNeighborhood: basicData.addressNeighborhood,
        documentNumber: basicData.documentNumber,
        openingHours: basicData.openingHours,
        description: basicData.description,
        lat: basicData.lat,
        long: basicData.long
      });
      
      if (response.data?.error) {
        throw new Error('Erro ao atualizar dados básicos');
      }

      return {
        success: true,
        entity: response.data.response
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao atualizar dados básicos',
      };
    }
  }
};
